import React, {FC, memo, useEffect, useRef, useState} from "react";
import {Maybe} from "type-graphql";
import {Button, Form, InputNumber, Space} from "antd";
import {ArrayDataOptions, FilterOption} from "../../generated/graphql";

type FilterDropdownAgeProps = {
    dataIndex: string;
    options?: Maybe<ArrayDataOptions>;
    setOptions?: (options: ArrayDataOptions) => void;
    setDropdownOpen?: (dropdownOpen: string | null) => void;
};

type FilterMode = "range" | "gte" | "lte" | "eq";

// Konstanten für Alter-Filter (Werte in Gesamtmonaten gespeichert)
const MIN_MONTHS = 0;
const MAX_MONTHS = 1200; // 100 Jahre - obere Begrenzung
const MAX_MONTH_PART = 11; // Monate 0-11

/**
 * Wandelt Gesamtmonate in {years, months} um.
 */
const monthsToYearsMonths = (totalMonths: number): {years: number; months: number} => {
    const safe = Math.max(0, Math.floor(totalMonths));
    return {
        years: Math.floor(safe / 12),
        months: safe % 12,
    };
};

/**
 * Wandelt {years, months} in Gesamtmonate um.
 */
const yearsMonthsToMonths = (years: number, months: number): number => {
    const y = Math.max(0, Math.floor(years));
    const m = Math.max(0, Math.min(MAX_MONTH_PART, Math.floor(months)));
    return y * 12 + m;
};

const presets: Array<{key: string; text: string; min: number; max: number}> = [
    {key: "vorschule", text: "Vorschule", min: 0, max: 60}, // 0-5 J
    {key: "grundschule", text: "Grundschule", min: 72, max: 132}, // 6-10 J 11 M
    {key: "teenager", text: "Teenager", min: 132, max: 216}, // 11-17 J 11 M
    {key: "erwachsene", text: "Erwachsene", min: 216, max: MAX_MONTHS}, // 18+
];

const FilterDropdownAge: FC<FilterDropdownAgeProps> = ({dataIndex, options, setOptions, setDropdownOpen}) => {
    // selectedKeys[0] = minMonths als String, selectedKeys[1] = maxMonths als String
    const [selectedKeys, setSelectedKeys] = useState<Array<string>>(["", ""]);
    const [filterMode, setFilterMode] = useState<FilterMode>("range");

    // Refs für Tab-Navigation
    const minYearsRef = useRef<HTMLInputElement>(null);
    const minMonthsRef = useRef<HTMLInputElement>(null);
    const maxYearsRef = useRef<HTMLInputElement>(null);
    const maxMonthsRef = useRef<HTMLInputElement>(null);

    const panelWidth = 360;
    const panelPadding = 12;

    // Initialisiere aus aktiven Filter-Values
    const currentFilter = options?.filter?.find((filter) => filter.column === dataIndex);
    const currentValues = currentFilter?.values ?? [];

    useEffect(() => {
        if (currentValues.length >= 2) {
            setSelectedKeys([currentValues[0] ?? "", currentValues[1] ?? ""]);

            // Filter-Modus aus aktiven Values erkennen
            const min = currentValues[0];
            const max = currentValues[1];
            if (min === max) {
                setFilterMode("eq");
            } else if (min !== String(MIN_MONTHS) && max === String(MAX_MONTHS)) {
                setFilterMode("gte");
            } else if (min === String(MIN_MONTHS) && max !== String(MAX_MONTHS)) {
                setFilterMode("lte");
            } else {
                setFilterMode("range");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFilter?.column]);

    const minTotalMonths = selectedKeys[0] !== "" ? Number(selectedKeys[0]) : undefined;
    const maxTotalMonths = selectedKeys[1] !== "" ? Number(selectedKeys[1]) : undefined;

    const minParts = monthsToYearsMonths(minTotalMonths ?? 0);
    const maxParts = monthsToYearsMonths(maxTotalMonths ?? 0);

    const setNewFilterOption = (column: string, values?: Array<string>) => {
        if (!values || values.length === 0) return;

        // Wenn dataIndex bereits existiert, ersetzen
        if (options?.filter?.find((filter) => filter.column === dataIndex)) {
            const updatedFilters = options.filter.map((filter) => {
                if (filter.column === dataIndex) {
                    return {
                        ...filter,
                        values,
                    };
                }

                return filter;
            });
            const updatedOptions = {
                ...options,
                filter: updatedFilters,
            };

            setOptions?.(updatedOptions);
            setDropdownOpen?.(null);

            return;
        }

        const newFilter: FilterOption = {
            column: "age",
            values: [...values],
            table: "bookings",
        };

        const updatedOptions = {
            ...options,
            filter: [...(options?.filter ?? []), newFilter],
        };

        setOptions?.(updatedOptions);
        setDropdownOpen?.(null);
    };

    const resetSelectedFilter = (column: string) => {
        const updatedFilters = options?.filter?.filter((el) => {
            return el.column !== column;
        });
        const updatedOptions = {
            ...options,
            filter: updatedFilters,
        };

        setOptions?.(updatedOptions);
        setDropdownOpen?.(null);
    };

    /**
     * Aktualisiert min oder max Total-Monate basierend auf Jahre/Monate-Eingabe.
     * field: 0 = min, 1 = max
     * part: "years" oder "months"
     */
    const handleYearsMonthsChange = (value: React.Key | null, field: 0 | 1, part: "years" | "months") => {
        const numericValue = value === null ? 0 : Number(value);

        const current = field === 0 ? minParts : maxParts;
        const updatedYears = part === "years" ? numericValue : current.years;
        const updatedMonths = part === "months" ? numericValue : current.months;

        const newTotal = yearsMonthsToMonths(updatedYears, updatedMonths);

        const updatedSelectedKeys = selectedKeys.length >= 2 ? [...selectedKeys] : ["", ""];
        updatedSelectedKeys[field] = String(newTotal);
        setSelectedKeys(updatedSelectedKeys);
    };

    const handleApply = () => {
        let values: Array<string> = [];

        // Für Single-Input-Modi (gte, lte, eq) verwenden wir minTotalMonths
        const inputValue = selectedKeys[0] !== "" ? selectedKeys[0] : "";

        switch (filterMode) {
            case "range":
                values = [
                    selectedKeys[0] !== "" ? selectedKeys[0] : String(MIN_MONTHS),
                    selectedKeys[1] !== "" ? selectedKeys[1] : String(MAX_MONTHS),
                ];
                break;
            case "gte":
                if (inputValue === "") {
                    console.warn("Kein Wert für ≥ Filter eingegeben");
                    return;
                }
                values = [inputValue, String(MAX_MONTHS)];
                break;
            case "lte":
                if (inputValue === "") {
                    console.warn("Kein Wert für ≤ Filter eingegeben");
                    return;
                }
                values = [String(MIN_MONTHS), inputValue];
                break;
            case "eq":
                if (inputValue === "") {
                    console.warn("Kein Wert für = Filter eingegeben");
                    return;
                }
                values = [inputValue, inputValue];
                break;
        }

        setNewFilterOption(dataIndex, values);
    };

    const isPresetActive = (preset: {min: number; max: number}): boolean => {
        if (filterMode !== "range") return false;
        return minTotalMonths === preset.min && maxTotalMonths === preset.max;
    };

    const renderYearsMonthsInput = (field: 0 | 1, label: string, yearsRef: React.RefObject<HTMLInputElement>, monthsRef: React.RefObject<HTMLInputElement>, onTabNext?: () => void, onShiftTabPrev?: () => void) => {
        const parts = field === 0 ? minParts : maxParts;
        const hasValue = selectedKeys[field] !== "";

        return (
            <Form.Item label={label} style={{marginBottom: 0}} colon={false}>
                <Space size={4}>
                    <InputNumber
                        ref={yearsRef}
                        size="small"
                        style={{width: 64}}
                        min={0}
                        max={Math.floor(MAX_MONTHS / 12)}
                        controls={false}
                        value={hasValue ? parts.years : undefined}
                        onChange={(e) => handleYearsMonthsChange(e as unknown as React.Key, field, "years")}
                        onPressEnter={handleApply}
                        onKeyDown={(e) => {
                            if (e.key === "Tab" && !e.shiftKey) {
                                e.preventDefault();
                                monthsRef.current?.focus();
                            } else if (e.key === "Tab" && e.shiftKey && onShiftTabPrev) {
                                e.preventDefault();
                                onShiftTabPrev();
                            }
                        }}
                        addonAfter="J"
                    />
                    <InputNumber
                        ref={monthsRef}
                        size="small"
                        style={{width: 64}}
                        min={0}
                        max={MAX_MONTH_PART}
                        controls={false}
                        value={hasValue ? parts.months : undefined}
                        onChange={(e) => handleYearsMonthsChange(e as unknown as React.Key, field, "months")}
                        onPressEnter={handleApply}
                        onKeyDown={(e) => {
                            if (e.key === "Tab" && !e.shiftKey && onTabNext) {
                                e.preventDefault();
                                onTabNext();
                            } else if (e.key === "Tab" && e.shiftKey) {
                                e.preventDefault();
                                yearsRef.current?.focus();
                            }
                        }}
                        addonAfter="M"
                    />
                </Space>
            </Form.Item>
        );
    };

    return (
        <div style={{width: panelWidth, padding: panelPadding, boxSizing: "border-box"}}>
            {/* Filter-Modus Auswahl - volle Breite */}
            <Button.Group size="small" style={{display: "flex", width: "100%", marginBottom: 12}}>
                <Button style={{flex: 1}} type={filterMode === "range" ? "primary" : "default"} onClick={() => setFilterMode("range")}>
                    {"Bereich"}
                </Button>
                <Button style={{flex: 1}} type={filterMode === "gte" ? "primary" : "default"} onClick={() => setFilterMode("gte")}>
                    {"≥"}
                </Button>
                <Button style={{flex: 1}} type={filterMode === "lte" ? "primary" : "default"} onClick={() => setFilterMode("lte")}>
                    {"≤"}
                </Button>
                <Button style={{flex: 1}} type={filterMode === "eq" ? "primary" : "default"} onClick={() => setFilterMode("eq")}>
                    {"="}
                </Button>
            </Button.Group>

            {filterMode === "range" ? (
                <Space size={12} style={{display: "flex", marginBottom: 8, justifyContent: "center"}}>
                    {renderYearsMonthsInput(
                        0,
                        "von",
                        minYearsRef,
                        minMonthsRef,
                        () => maxYearsRef.current?.focus(),
                        undefined,
                    )}
                    {renderYearsMonthsInput(
                        1,
                        "bis",
                        maxYearsRef,
                        maxMonthsRef,
                        undefined,
                        () => minMonthsRef.current?.focus(),
                    )}
                </Space>
            ) : (
                <div style={{marginBottom: 8, display: "flex", justifyContent: "center"}}>
                    {renderYearsMonthsInput(
                        0,
                        filterMode === "gte" ? "≥" : filterMode === "lte" ? "≤" : "=",
                        minYearsRef,
                        minMonthsRef,
                    )}
                </div>
            )}

            {/* Presets (nur sinnvoll im Bereich-Modus) */}
            {filterMode === "range" ? (
                <div
                    style={{
                        width: "100%",
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gridColumnGap: "8px",
                        gridRowGap: "5px",
                        marginBottom: "10px",
                        marginTop: 12,
                    }}
                >
                    {presets.map((preset) => (
                        <Button
                            size="small"
                            key={preset.key}
                            type={isPresetActive(preset) ? "primary" : "default"}
                            onClick={() => {
                                setSelectedKeys([String(preset.min), String(preset.max)]);
                            }}
                        >
                            {preset.text}
                        </Button>
                    ))}
                </div>
            ) : null}

            <Space direction="vertical" style={{width: "100%"}}>
                <Button size="small" type="primary" block onClick={handleApply}>
                    {"Übernehmen"}
                </Button>
                <Button
                    size="small"
                    block
                    danger
                    onClick={() => {
                        resetSelectedFilter(dataIndex);
                    }}
                >
                    {"Filter entfernen"}
                </Button>
            </Space>
        </div>
    );
};

export default memo(FilterDropdownAge);
